import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Warning, PaperPlaneTilt, Heart, Sparkle,
} from "@phosphor-icons/react";
import TrustBadges from "@/components/TrustBadges";
import ShareButton from "@/components/ShareButton";
import FollowButton from "@/components/FollowButton";
import { useAuth } from "@/contexts/AuthContext";
import LoginButton from "@/components/LoginButton";

function abs(u) { return !u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`; }

const REACTIONS = [
  { key: "like", emoji: "👍", label: "Like" },
  { key: "love", emoji: "❤️", label: "Love" },
  { key: "surprise", emoji: "😮", label: "Surprise" },
  { key: "care", emoji: "🤝", label: "Care" },
];

const COMMENT_KINDS = [
  { key: "comment", label: "Comment" },
  { key: "identification", label: "Identification" },
  { key: "correction", label: "Correction" },
  { key: "suggestion", label: "Suggestion" },
  { key: "question", label: "Question" },
  { key: "warning", label: "Warning" },
  { key: "useful-link", label: "Useful link" },
  { key: "official-response", label: "Official response" },
  { key: "update", label: "Update" },
];

// ------------- Reactions bar -------------
function ReactionBar({ endpoint, initial, onChange, testidRoot }) {
  const [reactions, setReactions] = useState(initial || {});
  const { user, requireSignIn } = useAuth();
  useEffect(() => setReactions(initial || {}), [initial]);
  const react = async (r) => {
    if (!requireSignIn("react to this observation")) return;
    try {
      const { data } = await http.post(endpoint, { reaction: r });
      setReactions(data.reactions || {});
      onChange?.(data.reactions);
    } catch { toast.error("Couldn't react"); }
  };
  return (
    <div className="flex flex-wrap gap-1">
      {REACTIONS.map((r) => (
        <button
          key={r.key}
          onClick={() => react(r.key)}
          data-testid={`${testidRoot}-react-${r.key}`}
          className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white/70 hover:bg-white px-2.5 py-1 text-xs"
          title={r.label}
        >
          <span>{r.emoji}</span>
          <span className="font-mono-tech text-[10px] tracking-widest">{reactions?.[r.key] || 0}</span>
        </button>
      ))}
    </div>
  );
}

// ------------- Comment card + reply thread -------------
function CommentCard({ c, replies, onReply, onDelete }) {
  const { user } = useAuth();
  const canDelete = user && (user.is_admin || user.firebase_uid === c.author?.user_id);
  return (
    <li className="rounded-xl border border-black/10 bg-white/70 p-4" data-testid={`comment-${c.id}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
          {c.author?.name || "Observer"} · {c.kind}
        </div>
        <div className="font-mono-tech text-[9px] tracking-[0.22em] text-[color:var(--np-muted,#3A3830)]">
          {(c.created_at || "").slice(0, 16).replace("T", " ")}
        </div>
      </div>
      {c.proposed && (c.proposed.common_name || c.proposed.scientific_name) && (
        <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-200 p-2 text-sm">
          <b>Proposed:</b> {c.proposed.common_name}
          {c.proposed.scientific_name && <> <i className="text-emerald-900/70">({c.proposed.scientific_name})</i></>}
        </div>
      )}
      <div className="mt-1.5 text-[15px] leading-snug whitespace-pre-wrap">{c.body}</div>
      {c.useful_link && (
        <a href={c.useful_link} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex text-sm text-[color:var(--np-brand,#B85F2A)] underline break-all">
          {c.useful_link}
        </a>
      )}
      <div className="mt-2 flex items-center justify-between gap-2">
        <ReactionBar
          endpoint={`/nature/comments/${c.id}/react`}
          initial={c.reactions}
          testidRoot={`comment-${c.id}`}
        />
        <div className="flex items-center gap-2">
          <button
            onClick={() => onReply(c.id)}
            data-testid={`comment-${c.id}-reply`}
            className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline"
          >Reply</button>
          {canDelete && (
            <button
              onClick={() => onDelete(c.id)}
              data-testid={`comment-${c.id}-delete`}
              className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-rose-700 hover:underline"
            >Delete</button>
          )}
        </div>
      </div>
      {replies?.length > 0 && (
        <ul className="mt-3 pl-4 border-l-2 border-black/10 space-y-2">
          {replies.map((r) => (
            <li key={r.id} className="rounded-lg bg-white/60 border border-black/10 p-3" data-testid={`reply-${r.id}`}>
              <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                {r.author?.name || "Observer"} · {r.kind}
              </div>
              <div className="mt-1 text-sm whitespace-pre-wrap">{r.body}</div>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

// ------------- Composer -------------
function Composer({ obsId, parentId, onPosted, onCancel }) {
  const { user } = useAuth();
  const [kind, setKind] = useState("comment");
  const [body, setBody] = useState("");
  const [propCommon, setPropCommon] = useState("");
  const [propSci, setPropSci] = useState("");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user) {
    return (
      <div className="rounded-xl border border-black/10 bg-white/70 p-4">
        <div className="text-sm text-[#3A3830]">Sign in to join the discussion.</div>
        <div className="mt-2"><LoginButton label="Sign in" /></div>
      </div>
    );
  }
  const submit = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      const payload = {
        kind, body,
        parent_id: parentId || null,
        useful_link: link || null,
        proposed_common: kind === "identification" ? propCommon || null : null,
        proposed_scientific: kind === "identification" ? propSci || null : null,
      };
      const { data } = await http.post(`/nature/observations/${obsId}/comments`, payload);
      onPosted?.(data);
      setBody(""); setPropCommon(""); setPropSci(""); setLink("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-black/10 bg-white/80 p-4" data-testid={parentId ? `composer-reply-${parentId}` : "composer-top"}>
      <div className="flex items-center gap-2 mb-2">
        <select
          value={kind} onChange={(e) => setKind(e.target.value)}
          data-testid="composer-kind"
          className="bg-transparent border border-black/15 rounded-full px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase"
        >
          {COMMENT_KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
        </select>
        {parentId && (
          <button
            onClick={onCancel}
            className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)] hover:text-[#151312]"
          >Cancel reply</button>
        )}
      </div>
      {kind === "identification" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <input value={propCommon} onChange={(e) => setPropCommon(e.target.value)} placeholder="Common name" className="bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="composer-common" />
          <input value={propSci} onChange={(e) => setPropSci(e.target.value)} placeholder="Scientific name (optional)" className="bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="composer-scientific" />
        </div>
      )}
      <textarea
        value={body} onChange={(e) => setBody(e.target.value)}
        rows={3} placeholder="Your comment or identification…"
        data-testid="composer-body"
        className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
      />
      {kind === "useful-link" && (
        <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" data-testid="composer-link" className="mt-2 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
      )}
      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          disabled={busy || !body.trim()}
          onClick={submit}
          data-testid="composer-submit"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50"
        >
          <PaperPlaneTilt size={12} weight="fill" /> Post
        </button>
      </div>
    </div>
  );
}

// ------------- Detail page -------------
export default function ObservationDetail() {
  const { id } = useParams();
  const [obs, setObs] = useState(null);
  const [comments, setComments] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadObs = () => http.get(`/nature/observations/${id}`).then((r) => setObs(r.data)).catch(() => setObs(null));
  const loadComments = () => http.get(`/nature/observations/${id}/comments`).then((r) => setComments(r.data?.comments || [])).catch(() => setComments([]));

  useEffect(() => {
    setLoading(true);
    Promise.all([loadObs(), loadComments()]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const threads = useMemo(() => {
    const roots = comments.filter((c) => !c.parent_id);
    const repliesByParent = comments.filter((c) => c.parent_id).reduce((acc, r) => {
      (acc[r.parent_id] ||= []).push(r); return acc;
    }, {});
    return { roots, repliesByParent };
  }, [comments]);

  const deleteComment = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await http.delete(`/nature/comments/${cid}`);
      loadComments();
    } catch { toast.error("Couldn't delete"); }
  };

  if (loading) {
    return <main className="min-h-screen pt-32 md:pt-36 pb-32 bg-[color:var(--np-bg,#F5EBDC)]"><div className="max-w-[900px] mx-auto px-5 md:px-10 font-mono-tech text-[10px] tracking-widest">Loading…</div></main>;
  }
  if (!obs) {
    return (
      <main className="min-h-screen pt-32 md:pt-36 pb-32 bg-[color:var(--np-bg,#F5EBDC)]">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <div className="font-serif-display text-2xl">Observation not found.</div>
          <Link to="/observations" className="mt-3 inline-flex items-center gap-1 text-[color:var(--np-brand,#B85F2A)]">
            <ArrowLeft size={14} weight="bold" /> Back to feed
          </Link>
        </div>
      </main>
    );
  }

  const media = obs.media?.[0]?.url;
  const kind = obs.category_kinds?.[0] || "nature";
  const safety = obs.category_path?.map((p) => p.safety_note).filter(Boolean).slice(-1)[0];
  const rp = obs.reporter?.public_alias || obs.reporter?.name || "Observer";

  return (
    <main className="bg-[color:var(--np-bg,#F5EBDC)] text-[color:var(--np-text,#151312)] min-h-screen pt-32 md:pt-36 pb-32" data-testid="observation-detail">
      <div className="max-w-[900px] mx-auto px-5 md:px-10">
        <Link to="/observations" className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-4">
          <ArrowLeft size={12} weight="bold" /> Back to feed
        </Link>

        {media && (
          <div className="rounded-2xl overflow-hidden border border-black/10 bg-black/5">
            <img src={abs(media)} alt={obs.title} className="w-full h-auto" />
          </div>
        )}
        <div className="mt-5 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
          {(obs.category_path || []).map((p) => p.label).join(" › ")}
        </div>
        <h1 className="mt-2 font-serif-display text-3xl md:text-5xl leading-[1.02] tracking-tight">{obs.title}</h1>
        <div className="mt-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)]">
          {obs.date} {obs.time && <>· {obs.time}</>}
        </div>
        <div className="mt-2"><TrustBadges labels={obs.trust_labels} /></div>

        <div className="mt-3 flex flex-wrap gap-2">
          <ShareButton
            title={obs.title}
            text={[obs.category_path?.map((p) => p.label).join(" › "), obs.location?.display_name].filter(Boolean).join(" · ")}
            hashtags={["NaturePulse", ...(obs.tags || []).slice(0, 3)]}
            shortKind="observation" shortId={obs.id}
            variant="chip" size="md"
            testid="observation-share"
          />
          <FollowButton
            type="observation" target={obs.id} label={obs.title}
            testid="follow-observation"
          />
          {obs.species?.scientific_name && (
            <FollowButton
              type="species"
              target={obs.species.scientific_name.toLowerCase()}
              label={obs.species.common_name || obs.species.scientific_name}
              testid="follow-species"
            />
          )}
          {(obs.location?.city || obs.location?.state || obs.location?.country) && (
            <FollowButton
              type="location"
              target={obs.location.city || obs.location.state || obs.location.country}
              label={[obs.location.city, obs.location.state, obs.location.country].filter(Boolean).join(", ")}
              testid="follow-location"
            />
          )}
        </div>

        {safety && (
          <div className="mt-4 rounded-lg bg-amber-50 border border-amber-300 text-amber-900 p-3 text-sm flex gap-2">
            <Warning size={18} weight="fill" className="text-amber-600 shrink-0" />
            <div>{safety}</div>
          </div>
        )}
        {obs.description && <p className="mt-4 font-serif-display text-lg md:text-xl leading-relaxed">{obs.description}</p>}

        {/* Location panel */}
        {obs.location && (
          <div className="mt-5 rounded-xl border border-black/10 bg-white/70 p-4">
            <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-1 flex items-center gap-1">
              <MapPin size={12} weight="fill" /> Location
            </div>
            <div className="font-serif-display text-lg">{obs.location.display_name || "—"}</div>
            <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
              {["country", "state", "county", "city", "village", "locality"].map((k) => (
                obs.location[k] ? <div key={k}><span className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)]">{k}</span> <span className="ml-1.5">{obs.location[k]}</span></div> : null
              ))}
            </div>
            {(obs.location.lat != null && obs.location.lon != null) && (
              <div className="mt-2 font-mono-tech text-[10px] tracking-[0.22em] text-[color:var(--np-muted,#3A3830)]">
                {obs.location.lat.toFixed(4)}, {obs.location.lon.toFixed(4)} · {obs.location.precision || "exact"}
              </div>
            )}
          </div>
        )}

        {/* Species */}
        {obs.species && (obs.species.common_name || obs.species.scientific_name) && (
          <div className="mt-4 rounded-xl border border-black/10 bg-white/70 p-4">
            <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-1">Species</div>
            <div className="font-serif-display text-lg">{obs.species.common_name}</div>
            {obs.species.scientific_name && <div className="italic text-sm text-[#3A3830]">{obs.species.scientific_name}</div>}
          </div>
        )}

        <div className="mt-5">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] mb-2">Reactions</div>
          <ReactionBar
            endpoint={`/nature/observations/${obs.id}/react`}
            initial={obs.reactions}
            testidRoot="observation"
          />
        </div>

        <div className="mt-3 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)]">
          Reported by {rp}
        </div>

        {/* Discussion */}
        <section className="mt-10">
          <h2 className="font-serif-display text-2xl md:text-3xl mb-4">Discussion</h2>
          <div className="mb-4"><Composer obsId={obs.id} onPosted={() => loadComments()} /></div>
          {threads.roots.length === 0 ? (
            <div className="text-sm text-[color:var(--np-muted,#3A3830)]">Be the first to comment.</div>
          ) : (
            <ul className="space-y-3">
              {threads.roots.map((c) => (
                <div key={c.id}>
                  <CommentCard c={c} replies={threads.repliesByParent[c.id]} onReply={setReplyTo} onDelete={deleteComment} />
                  {replyTo === c.id && (
                    <div className="mt-2 pl-6">
                      <Composer obsId={obs.id} parentId={c.id} onPosted={() => { setReplyTo(null); loadComments(); }} onCancel={() => setReplyTo(null)} />
                    </div>
                  )}
                </div>
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}
